import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { useAuth } from '../features/auth/AuthContext';
import { PasswordVisibilityToggle } from '../components/ui/Input';

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordRevealed, setIsPasswordRevealed] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const loggedInUser = await login(email, password);
      const destination =
        loggedInUser.role === 'ADMIN' || loggedInUser.role === 'ASSISTANT'
          ? '/admin'
          : loggedInUser.role === 'TEACHER'
            ? '/teacher'
            : '/dashboard';
      navigate(destination);
    } catch (err) {
      if (isAxiosError(err) && err.response?.data?.message === 'EMAIL_NOT_VERIFIED') {
        navigate(`/check-email?email=${encodeURIComponent(email)}`);
        return;
      }
      if (isAxiosError(err) && err.response?.data?.message === 'PASSWORD_NOT_SET') {
        setError(t('auth.login.passwordNotSet'));
        return;
      }
      setError(t('auth.login.error'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-paper-100 px-4 font-manuscript">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #8c2d1e 1.5px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      />

      <div className="relative w-full max-w-sm">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
            <img src="/logo.png" alt={t('common.appName')} className="h-full w-full object-cover" />
          </span>
          <span className="font-display text-lg font-semibold text-manuscript-ink">{t('common.appName')}</span>
        </Link>

        <div className="rounded-sm border border-manuscript-line-strong bg-paper-50 p-8">
          <h1 className="font-display text-xl font-semibold text-manuscript-ink">{t('auth.login.title')}</h1>
          <p className="mt-1 text-sm text-manuscript-ink-soft">{t('auth.login.subtitle')}</p>

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
            <div>
              <label htmlFor="email" className="text-sm text-manuscript-ink-soft">
                {t('common.email')}
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-sm border border-manuscript-line-strong bg-paper-100 px-4 py-2.5 text-sm text-manuscript-ink outline-none focus:border-seal-500"
              />
            </div>
            <div>
              <label htmlFor="password" className="text-sm text-manuscript-ink-soft">
                {t('auth.login.password')}
              </label>
              <div className="relative mt-1.5">
                <input
                  id="password"
                  type={isPasswordRevealed ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-sm border border-manuscript-line-strong bg-paper-100 px-4 py-2.5 pr-10 text-sm text-manuscript-ink outline-none focus:border-seal-500"
                />
                <PasswordVisibilityToggle
                  isRevealed={isPasswordRevealed}
                  onToggle={() => setIsPasswordRevealed((prev) => !prev)}
                />
              </div>
              <Link
                to="/forgot-password"
                className="mt-1.5 inline-block text-xs font-medium text-seal-500 hover:text-seal-400"
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-2 flex w-full items-center justify-center rounded-sm bg-seal-500 px-6 py-3 text-sm font-semibold text-paper-50 transition-colors hover:bg-seal-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? t('auth.login.submitting') : t('auth.login.submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
